<template>
	<div class="content-page content-page--integration-manager">
		<div class="content-page__heading">
			<div class="content-page__breadcrumb-title">
				<h2 class="content-page__title">Third Party Manager</h2>
			</div>
			<div class="content-page__actions">
				<Button
					className="btn--has-icon"
					iconClassName="icon-more"
					labelText="Add"
					:btnClick="addThirdParty"
					:iconBeforeLabel="true"
					:disabled="!canCreate"
				/>
			</div>
		</div>
		<div class="results-toolbar results-toolbar--search-simple-filter">
			<div class="results-toolbar__search">
				<Search
					v-model="filters.search"
					variant="toggle-icons"
					placeholder="Search by vendor or integration"
					ref="searchRef"
				/>
			</div>
			<div class="results-toolbar__filter">
				<DropdownV2
					:options="statusTypeOptions"
					v-model="filters.status"
					defaultValue="active"
				/>
			</div>
			<div class="results-toolbar__counter">
				<ResultCounter :counter="resultCounter" />
			</div>
		</div>
		<ScrollContainer
			v-if="hasResults"
			variant="infinity-scroll"
			:hasMoreItems="hasMoreItemsToLoad"
			@scrollEnd="handleScrollEnd"
		>
			<IntegrationList
				@editThirdParty="thirdPartySettings"
				:items="thirdparties.items"
			/>
		</ScrollContainer>
		<ThirdPartyFlyout
			@save="fetchData()"
			v-model="displayNewOrEditThirdPartyFlyout"
			:source="thirdParty"
			:thirdparties="thirdparties"
			ref="thirdPartyFlyoutRef"
		/>
		<Spinner
			v-if="loading"
			:processor="{ loading: true }"
			ref="spinnerRef"
		/>
		<NoResults v-if="!loading && !hasResults" data-test="noResults" />
	</div>
</template>
<script>
import Button from '@/components/forms/button/button';
import DropdownV2 from '@/components/forms/dropdown/dropdown-v2.vue';
import ResultCounter from '@/site-components/result-counter/result-counter';
import Search from '@/components/forms/search/search';
import IntegrationList from './thirdparties-list';
import ThirdPartyFlyout from './thirdparty-flyout';
import Spinner from '@/components/visual/spinner/spinner';
import NoResults from '@/site-components/no-results/no-results';
import ScrollContainer from '@/components/shells/scroll-container/scroll-container';
import { getDifferentValues } from '@/utils/function';
// mixins
import ValidatePermissions from '@/sections/integration/validate-permission-mixins';
// libraries
import _ from 'lodash';
// vuex
import { mapState, mapActions, mapGetters } from 'vuex';

export default {
	name: 'ThirdParties',
	mixins: [ValidatePermissions],
	components: {
		Button,
		DropdownV2,
		IntegrationList,
		ResultCounter,
		Search,
		ThirdPartyFlyout,
		Spinner,
		NoResults,
		ScrollContainer
	},
	props: {
		search: {
			type: String
		},
		status: {
			type: String
		},
		sortBy: {
			type: String
		},
		sortDirection: {
			type: String
		},
		thirdPartyId: {
			type: String
		},
		count: {
			type: Number
		}
	},
	data() {
		return {
			filters: {
				search: this.search,
				status: this.status,
				sortBy: this.sortBy,
				sortDirection: this.sortDirection
			},
			page: 1,
			countPerPage: this.count,
			defaultFilters: {
				search: undefined,
				status: 'active',
				sortBy: 'name',
				sortDirection: 'asc'
			},
			displayNewOrEditThirdPartyFlyout: false,
			statusTypeOptions: [
				{
					text: 'All Status',
					value: 'all'
				},
				{
					text: 'Active',
					value: 'active'
				},
				{
					text: 'Inactive',
					value: 'inactive'
				}
			],
			thirdParty: {},
			loading: true,
			loadingMore: false,
			thirdPartyIdData: 0
		};
	},
	computed: {
		...mapState('integrations', ['thirdparties']),
		...mapGetters('integrations', ['thirdPartiesById']),
		hasResults() {
			return this.thirdparties.items?.length;
		},
		resultCounter() {
			if (!this.thirdparties.items) {
				return '0';
			}

			return `${this.thirdparties.items?.length} of ${this.thirdparties.totalCount}`;
		},
		hasMoreItemsToLoad() {
			return (
				this.thirdparties.items?.length < this.thirdparties.totalCount
			);
		}
	},
	methods: {
		...mapActions('integrations', ['getThirdParties', 'getThirdPartyById']),
		addThirdParty() {
			this.thirdParty = {};
			this.displayNewOrEditThirdPartyFlyout = true;
		},
		thirdPartySettings(thirdPartyId) {
			this.thirdParty = this.thirdPartiesById(thirdPartyId);
			if (this.thirdParty.thirdPartyId) {
				this.thirdPartyIdData = this.thirdParty.thirdPartyId;
			}
			this.displayNewOrEditThirdPartyFlyout = true;
		},
		async fetchData(page) {
			let query = _.cloneDeep(this.filters);
			query.page = page;
			query.count = this.countPerPage || 100;

			if (!this.loadingMore) {
				this.loading = true;
			}
			if (query.status) {
				query.active =
					query.status === 'all'
						? null
						: query.status === 'active'
						? true
						: false;
			}
			delete query.status;

			await this.getThirdParties(query);
			this.loading = false;
			this.loadingMore = false;
		},
		updateRoute(queries) {
			let queryObj = getDifferentValues(this.defaultFilters, queries);

			if (this.displayNewOrEditThirdPartyFlyout) {
				if (queries.thirdPartyId) {
					queryObj.thirdPartyId = this.thirdPartyIdData;
				}
			} else {
				delete queryObj.thirdPartyId;
			}

			this.$router
				.push({
					path: '/integrations/thirdparties',
					query: queryObj
				})
				.catch(() => {});
		},
		handleScrollEnd() {
			if (this.hasMoreItemsToLoad) {
				this.loadingMore = true;
				this.page += 1;
			}
		},
		closeThirdPartyFlyout() {
			this.thirdParty = {};
			this.thirdPartyIdData = 0;
		}
	},
	watch: {
		filters: {
			deep: true,
			immediate: true,
			handler(newValue) {
				this.updateRoute(newValue);
				if (this.page != 1) {
					this.page = 1;
				} else {
					this.fetchData(1);
				}
			}
		},
		page: {
			handler(page) {
				this.fetchData(page);
			}
		},
		thirdPartyId: {
			immediate: true,
			handler(val) {
				if (val) {
					this.thirdPartyIdData = parseInt(val);
					let storedThirdParty = this.thirdPartiesById(
						this.thirdPartyIdData
					);
					if (
						_.isEmpty(this.thirdParty) &&
						!_.isEmpty(storedThirdParty)
					) {
						this.thirdParty = storedThirdParty;
						this.displayNewOrEditThirdPartyFlyout = true;
					}
					if (
						_.isEmpty(this.thirdParty) &&
						_.isEmpty(storedThirdParty)
					) {
						this.getThirdPartyById(val).then(res => {
							this.thirdParty = _.cloneDeep(res);
							this.displayNewOrEditThirdPartyFlyout = true;
						});
					}
				}
			}
		},
		displayNewOrEditThirdPartyFlyout: {
			handler(val) {
				if (val && this.thirdPartyIdData > 0) {
					this.updateRoute({
						...this.filters,
						thirdPartyId: this.thirdPartyIdData
					});
				} else {
					this.closeThirdPartyFlyout();
					this.updateRoute(this.filters);
				}
			}
		},
		'$route.query': {
			immediate: true,
			handler(newQuery) {
				if (!newQuery.thirdPartyId) {
					let newfilters = { ...this.defaultFilters, ...newQuery };
					if (!_.isEqual(newfilters, this.filters)) {
						this.filters = _.cloneDeep(newfilters);
					}
					this.displayNewOrEditThirdPartyFlyout = false;
				}
			}
		}
	}
};
</script>
